import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Check, ChevronDown } from 'lucide-react';

interface StrategyOption<T extends string> {
  code: T;
  labelKey: string;
}

interface SupplyFillStrategyPickerProps<T extends string> {
  value: T;
  options: ReadonlyArray<StrategyOption<T>>;
  onChange: (code: T) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

/** Espera antes de mostrar la explicación al dejar el ratón quieto sobre el control o una opción. */
const TOOLTIP_DELAY_MS = 450;
const PREVIEW_DELAY_MS = 250;

/**
 * Selector de estrategia de llenado. Al dejar el ratón sobre el control, o sobre cada opción de la lista,
 * explica qué hace la estrategia, cuándo usarla y qué tener en cuenta, para decidir antes de elegir.
 */
export function SupplyFillStrategyPicker<T extends string>({ value, options, onChange, t }: SupplyFillStrategyPickerProps<T>) {
  const [open, setOpen] = useState(false);
  const [tipVisible, setTipVisible] = useState(false);
  const [active, setActive] = useState(0);
  const [preview, setPreview] = useState<T>(value);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const previewTimer = useRef<number | undefined>(undefined);
  const tipTimer = useRef<number | undefined>(undefined);
  const listId = useId();

  const selectedOption = options.find((option) => option.code === value) ?? options[0];

  const schedulePreview = useCallback((code: T, delay: number) => {
    window.clearTimeout(previewTimer.current);
    if (delay <= 0) {
      setPreview(code);
      return;
    }
    previewTimer.current = window.setTimeout(() => setPreview(code), delay);
  }, []);

  const hideTip = useCallback(() => {
    window.clearTimeout(tipTimer.current);
    setTipVisible(false);
  }, []);

  useEffect(() => () => {
    window.clearTimeout(previewTimer.current);
    window.clearTimeout(tipTimer.current);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);

  const openMenu = () => {
    hideTip();
    const index = Math.max(0, options.findIndex((option) => option.code === value));
    setActive(index);
    schedulePreview(value, 0);
    setOpen(true);
  };

  const choose = (code: T) => {
    onChange(code);
    setOpen(false);
    buttonRef.current?.focus();
  };

  const moveActive = (delta: number) => {
    const next = (active + delta + options.length) % options.length;
    setActive(next);
    schedulePreview(options[next].code, 0);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openMenu();
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveActive(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveActive(-1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      choose(options[active].code);
    } else if (event.key === 'Escape' || event.key === 'Tab') {
      setOpen(false);
    }
  };

  const infoCard = (code: T) => {
    const label = options.find((option) => option.code === code)?.labelKey;
    return (
      <div className="space-y-1.5 text-xs text-slate-700">
        <p>
          <span className="font-semibold text-slate-900">{label ? t(label) : code}: </span>
          {t(`supply.supplyAgent.fillToTargetStrategyInfo.${code}.what`)}
        </p>
        <p>
          <span className="font-semibold text-emerald-800">{t('supply.supplyAgent.fillToTargetStrategyWhenLabel')} </span>
          {t(`supply.supplyAgent.fillToTargetStrategyInfo.${code}.when`)}
        </p>
        <p className="text-slate-600">
          <span className="font-semibold text-amber-800">{t('supply.supplyAgent.fillToTargetStrategyNoteLabel')} </span>
          {t(`supply.supplyAgent.fillToTargetStrategyInfo.${code}.note`)}
        </p>
        <p className="border-t border-slate-100 pt-1.5 text-[11px] text-slate-500">{t('supply.supplyAgent.fillToTargetStrategyHelp')}</p>
      </div>
    );
  };

  const popoverClass = 'absolute left-0 top-full z-30 mt-1 w-80 max-w-[calc(100vw-2rem)] rounded-md border border-slate-200 bg-white p-3 shadow-lg';

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-describedby={tipVisible && !open ? `${listId}-tip` : undefined}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
        onMouseEnter={() => {
          if (open) return;
          window.clearTimeout(tipTimer.current);
          tipTimer.current = window.setTimeout(() => setTipVisible(true), TOOLTIP_DELAY_MS);
        }}
        onMouseLeave={hideTip}
        onFocus={() => {
          if (open) return;
          window.clearTimeout(tipTimer.current);
          tipTimer.current = window.setTimeout(() => setTipVisible(true), TOOLTIP_DELAY_MS);
        }}
        onBlur={hideTip}
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-200"
      >
        <span>{t('supply.supplyAgent.fillToTargetStrategyLabel')}</span>
        <span className="font-semibold text-slate-900">{t(selectedOption.labelKey)}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {tipVisible && !open ? (
        <div id={`${listId}-tip`} role="tooltip" className={popoverClass}>
          {infoCard(value)}
        </div>
      ) : null}
      {open ? (
        <div className={popoverClass}>
          <ul id={listId} role="listbox" aria-activedescendant={`${listId}-${options[active].code}`} className="-mx-1 mb-2 space-y-0.5">
            {options.map((option, index) => {
              const selected = option.code === value;
              return (
                <li
                  key={option.code}
                  id={`${listId}-${option.code}`}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => {
                    setActive(index);
                    schedulePreview(option.code, PREVIEW_DELAY_MS);
                  }}
                  onClick={() => choose(option.code)}
                  className={`flex cursor-pointer items-center justify-between rounded px-2 py-1.5 text-xs ${index === active ? 'bg-emerald-50 text-emerald-900' : 'text-slate-700'} ${selected ? 'font-semibold' : ''}`}
                >
                  {t(option.labelKey)}
                  {selected ? <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" /> : null}
                </li>
              );
            })}
          </ul>
          <div className="border-t border-slate-100 pt-2">{infoCard(preview)}</div>
        </div>
      ) : null}
    </div>
  );
}
